(() => {
  const sectionSelector = '[data-section-entrance]';
  const itemSelector = '[data-entrance-item]';
  const readyValue = 'true';
  const visibleValue = 'visible';
  const defaultInitialBehavior = 'auto';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const defaultOptions = {
    threshold: 0.18,
    rootMargin: '0px 0px -12% 0px',
    initialDelay: 40
  };

  const presetOptions = {
    'editorial-statement': {
      initialDelay: 40,
      duration: '900ms',
      delayStep: '130ms',
      distance: 'clamp(1.8rem, 3vw, 3.2rem)',
      scale: '0.985',
      loadDuration: '680ms',
      loadDelayStep: '80ms',
      loadDistance: 'clamp(0.7rem, 1.3vw, 1.1rem)'
    },
    'editorial-image-strip': {
      initialDelay: 40,
      duration: '980ms',
      delayStep: '120ms',
      distance: 'clamp(2rem, 4vw, 4rem)',
      scale: '0.985',
      loadDuration: '760ms',
      loadDelayStep: '90ms',
      loadDistance: 'clamp(0.9rem, 1.6vw, 1.4rem)'
    },
    'split-image-pair': {
      initialDelay: 40,
      duration: '920ms',
      delayStep: '140ms',
      distance: 'clamp(1.8rem, 3vw, 3rem)',
      scale: '0.987',
      loadDuration: '700ms',
      loadDelayStep: '80ms',
      loadDistance: 'clamp(0.6rem, 1.1vw, 1rem)'
    },
    'custom-logo-list': {
      initialDelay: 55,
      duration: '760ms',
      delayStep: '55ms',
      distance: 'clamp(1.2rem, 2vw, 2.2rem)',
      scale: '0.985',
      loadDuration: '480ms',
      loadDelayStep: '34ms',
      loadDistance: 'clamp(0.45rem, 0.9vw, 0.8rem)'
    }
  };

  const observerCache = new Map();

  function revealSection(section) {
    if (!section || section.dataset.sectionEntranceState === visibleValue) {
      return;
    }

    window.requestAnimationFrame(() => {
      section.dataset.sectionEntranceState = visibleValue;
    });
  }

  function parseNumber(value, fallbackValue) {
    const parsedValue = Number.parseFloat(value);
    return Number.isFinite(parsedValue) ? parsedValue : fallbackValue;
  }

  function getSectionPreset(section) {
    return presetOptions[section.dataset.sectionEntrance] || {};
  }

  function getSectionOptions(section) {
    const preset = getSectionPreset(section);

    return {
      threshold: parseNumber(section.dataset.sectionEntranceThreshold, defaultOptions.threshold),
      rootMargin: section.dataset.sectionEntranceRootMargin || defaultOptions.rootMargin,
      initialDelay: parseNumber(
        section.dataset.sectionEntranceInitialDelay,
        preset.initialDelay ?? defaultOptions.initialDelay
      ),
      initialBehavior: section.dataset.sectionEntranceInitialBehavior || defaultInitialBehavior,
      duration: section.dataset.sectionEntranceDuration || preset.duration,
      delayStep: section.dataset.sectionEntranceDelayStep || preset.delayStep,
      distance: section.dataset.sectionEntranceDistance || preset.distance,
      scale: section.dataset.sectionEntranceScale || preset.scale,
      loadDuration: section.dataset.sectionEntranceLoadDuration || preset.loadDuration,
      loadDelayStep: section.dataset.sectionEntranceLoadDelayStep || preset.loadDelayStep,
      loadDistance: section.dataset.sectionEntranceLoadDistance || preset.loadDistance
    };
  }

  function applySectionMotionVariables(section, options) {
    const styleMap = {
      '--section-entrance-duration': options.duration,
      '--section-entrance-delay-step': options.delayStep,
      '--section-entrance-distance': options.distance,
      '--section-entrance-scale': options.scale,
      '--section-entrance-load-duration': options.loadDuration,
      '--section-entrance-load-delay-step': options.loadDelayStep,
      '--section-entrance-load-distance': options.loadDistance
    };

    Object.entries(styleMap).forEach(([name, value]) => {
      if (value && !section.style.getPropertyValue(name)) {
        section.style.setProperty(name, value);
      }
    });
  }

  function isInitiallyInView(section) {
    const rect = section.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const guardBand = Math.max(viewportHeight * 0.08, 48);

    return rect.top < viewportHeight - guardBand && rect.bottom > guardBand;
  }

  function markSectionReady(section, initialState) {
    section.dataset.sectionEntranceInitial = initialState;
    section.dataset.sectionEntranceReady = readyValue;
  }

  function queueInitialReveal(section, delay) {
    const runReveal = () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          revealSection(section);
        });
      });
    };

    if (delay > 0) {
      window.setTimeout(runReveal, delay);
      return;
    }

    runReveal();
  }

  function getObserver(options) {
    const cacheKey = `${options.threshold}|${options.rootMargin}`;

    if (observerCache.has(cacheKey)) {
      return observerCache.get(cacheKey);
    }

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          revealSection(entry.target);
          currentObserver.unobserve(entry.target);
        });
      },
      {
        threshold: options.threshold,
        rootMargin: options.rootMargin
      }
    );

    observerCache.set(cacheKey, observer);
    return observer;
  }

  function prepareSection(section) {
    if (!section || section.dataset.sectionEntranceReady === readyValue) {
      return;
    }

    const items = section.querySelectorAll(itemSelector);

    if (!items.length) {
      return;
    }

    const options = getSectionOptions(section);

    items.forEach((item, index) => {
      if (!item.style.getPropertyValue('--section-entrance-order')) {
        item.style.setProperty('--section-entrance-order', index);
      }
    });

    applySectionMotionVariables(section, options);

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      markSectionReady(section, 'reduced');
      section.dataset.sectionEntranceState = visibleValue;
      return;
    }

    if (options.initialBehavior === 'visible') {
      markSectionReady(section, 'visible');
      section.dataset.sectionEntranceState = visibleValue;
      return;
    }

    if (options.initialBehavior !== 'offscreen' && isInitiallyInView(section)) {
      markSectionReady(section, 'in-view');
      queueInitialReveal(section, options.initialDelay);
      return;
    }

    markSectionReady(section, 'offscreen');
    getObserver(options).observe(section);
  }

  function initSections(root = document) {
    if (!root.querySelectorAll) {
      return;
    }

    root.querySelectorAll(sectionSelector).forEach(prepareSection);
  }

  function onReady() {
    initSections();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initSections(event.target);
  });
})();
